"use client"

import IconCheck from "@/app/(site)/_components/Atoms/Icons/IconCheck"
import { type FontStylesWithoutDot } from "@/config/fontConfig/fontStyles"
import { clsx } from "@/src/utils/clsx"
import React, { useEffect, useState } from "react"

interface CheckboxProps {
    name: string
    label: string
    defaultValue?: boolean
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    required?: boolean
    className?: string
    labelClassName?: string
    id?: string
    typography?: FontStylesWithoutDot
    inseta11y?: { before: string }
}

export default function Checkbox({
    name,
    label,
    defaultValue = false,
    onChange,
    disabled = false,
    required = false,
    className = "",
    labelClassName = "",
    id,
    typography = "Trade_Gothic_Text_14_150_0_300",
    inseta11y = {
        before: "" //"before:absolute before:inset-0 inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']" Helps with a11y button press areas
    },
    ...props
}: CheckboxProps) {
    const checkboxId = id || `${name}-checkbox`

    const [checked, setChecked] = useState(defaultValue)

    useEffect(() => {
        setChecked(defaultValue)
    }, [defaultValue])

    return (
        <label
            id={`${checkboxId}-label`}
            htmlFor={checkboxId}
            className={clsx(
                "relative flex w-fit cursor-pointer select-none items-center gap-4 text-col-text-primary",
                inseta11y.before,
                disabled && "cursor-not-allowed",
                labelClassName,
                typography
            )}
        >
            <div className="relative flex items-center">
                <input
                    type="checkbox"
                    id={checkboxId}
                    name={name}
                    checked={checked}
                    onChange={e => {
                        setChecked(e.target.checked)
                        onChange(e)
                    }}
                    disabled={disabled}
                    required={required}
                    className={clsx(
                        "no-rounded-corners h-4 w-4 flex-shrink-0 cursor-pointer border-[1px] transition-colors duration-200",
                        checked
                            ? "border-none bg-col-bg-secondary"
                            : "border-col-border-neutral bg-col-bg-primary",
                        disabled && "cursor-not-allowed opacity-30"
                    )}
                    style={{ width: "16px", height: "16px" }}
                    aria-describedby={label ? `${checkboxId}-label` : undefined}
                    {...props}
                />

                {/* Decorative: the tick sits over the middle of the input, so it has to
                    stay out of hit testing or it swallows clicks aimed at the box. */}
                {checked && (
                    <IconCheck className="pointer-events-none absolute left-1/2 right-1/2 top-1/2 z-10 h-10 w-10 -translate-x-1/2 -translate-y-1/2 text-col-icon-inverted" />
                )}
            </div>

            <span className="pt-4">
                {label}
                {required && <span className="ml-1 text-col-text-alert">*</span>}
            </span>
        </label>
    )
}
